<template>
  <div class="relative py-3 mb-4 nav-bar-root" style="border-bottom: 1px solid var(--brass-rule, #e0d8c6);">
    <!-- 展开面板：年 + 月 合并单行（仅 jumpPanelOpen 时显示，硬约束不换行） -->
    <div v-if="jumpPanelOpen" class="flex justify-center mb-3">
      <div
        class="date-panel flex items-center space-x-2 overflow-x-auto whitespace-nowrap scrollbar-hide"
        style="max-width: 92%;"
        role="group"
        :aria-label="$t('interface.dateNav.year') + ' / ' + $t('interface.dateNav.month')"
      >
        <span
          v-for="year in yearOptions"
          :key="'y' + year"
          @click="selectYear(year)"
          :class="year === panelYear ? 'date-span-active' : 'date-span'"
          class="snap-center"
        >{{ year }}</span>
        <span class="date-divider" aria-hidden="true">|</span>
        <span
          v-for="month in monthOptions"
          :key="'m' + month.ym"
          @click="selectMonth(month.ym)"
          :class="month.ym === activeMonth ? 'date-span-active' : 'date-span'"
          class="snap-center"
          >{{ month.label }}</span>
      </div>
    </div>

    <!-- 日期筛选面板：与年月面板互斥（仅 datePanelOpen 时显示），仅展示当前周内有 featured_at 的日 -->
    <div v-if="datePanelOpen" class="flex justify-center mb-3">
      <div
        class="date-panel flex items-center space-x-2 overflow-x-auto whitespace-nowrap scrollbar-hide"
        style="max-width: 92%;"
        role="group"
        :aria-label="$t('interface.dateNav.selectDay')"
      >
        <span
          @click="clearDay"
          :class="activeDay === null ? 'date-span-active' : 'date-span'"
          class="snap-center"
          :aria-current="activeDay === null ? 'true' : 'false'"
        >{{ $t('interface.dateNav.allDays') }}</span>
        <span
          v-for="ymd in dayOptions"
          :key="ymd"
          @click="selectDay(ymd)"
          :class="activeDay === ymd ? 'date-span-active' : 'date-span'"
          class="snap-center"
          :aria-current="activeDay === ymd ? 'true' : 'false'"
        >{{ dayLabel(ymd) }}{{ dayCounts[ymd] != null ? ' (' + dayCounts[ymd] + ')' : '' }}</span>
      </div>
    </div>

    <!-- 周条行 + 切换按钮（常驻：仅此一行） -->
    <div class="flex justify-center items-center mx-auto" style="max-width: 92%;">
      <!-- 左滚动箭头：作为 date-scroll-wrap 的兄弟节点，不再 absolute 覆盖在内容上 -->
      <button
        v-if="isLeftOverflow"
        type="button"
        @click="scrollLeft"
        class="date-arrow-btn shrink-0 mr-2 flex items-center justify-center"
        style="width: 30px; height: 30px;"
        aria-label="向左滚动"
      ><i class="fas fa-chevron-left date-arrow-icon"></i></button>

      <!-- 周条滚动容器（flex-1，两侧箭头在布局流中提供天然留白） -->
      <div class="flex-1 date-scroll-wrap">
        <div
          class="flex space-x-4 overflow-x-auto whitespace-nowrap scrollbar-hide pr-3"
          ref="dateNavContainer"
          @scroll="checkOverflow"
        >
          <span
            v-for="range in dateRanges"
            :key="range"
            :ref="range"
            @click="selectWeek(range)"
            :class="{
              'date-span': selectedWeek !== range,
              'date-span-active': selectedWeek === range
            }"
            class="snap-center"
          >{{ formatRange(range) }}<span v-if="rangeCounts && rangeCounts[range] != null"> ({{ rangeCounts[range] }})</span></span>
          <!-- 全部时间 tab：位于周 tab 最右，仅搜索/标签页通过 showAllTimeTab 开启 -->
          <span
            v-if="showAllTimeTab"
            @click="selectAllTime"
            :class="selectedWeek === allTimeRange ? 'date-span-active' : 'date-span'"
            class="snap-center ml-3"
            :aria-current="selectedWeek === allTimeRange ? 'true' : 'false'"
          >{{ $t('interface.dateNav.allTime') }}<span v-if="allTimeCount != null"> ({{ allTimeCount }})</span></span>
          <!-- 尾部占位：确保最右日期条目与右侧箭头/按钮之间有足够留白 -->
          <span class="shrink-0" style="width: 16px; display: inline-block;" aria-hidden="true"></span>
        </div>
      </div>

      <!-- 右滚动箭头：兄弟节点，不再 absolute 覆盖在内容上 -->
      <button
        v-if="isRightOverflow"
        type="button"
        @click="scrollRight"
        class="date-arrow-btn shrink-0 ml-2 flex items-center justify-center"
        style="width: 30px; height: 30px;"
        aria-label="向右滚动"
      ><i class="fas fa-chevron-right date-arrow-icon"></i></button>

      <!-- 年月 / 收起 切换按钮（默认收起：显示「年月」；展开：显示「收起」） -->
      <button
        type="button"
        @click="toggleJumpPanel"
        class="date-jump-btn ml-3 shrink-0"
        :aria-expanded="jumpPanelOpen ? 'true' : 'false'"
      >{{ jumpPanelOpen ? $t('interface.dateNav.collapse') : $t('interface.dateNav.jump') }}</button>

      <!-- 日期 / 收起 切换按钮（与「年月」并列；当前周无 featured_at 时整按钮隐藏） -->
      <button
        v-if="dayPanelOpenable"
        type="button"
        @click="toggleDatePanel"
        class="date-jump-btn ml-2 shrink-0"
        :aria-expanded="datePanelOpen ? 'true' : 'false'"
        :title="$t('interface.dateNav.selectDay')"
      >{{ datePanelOpen ? $t('interface.dateNav.collapse') : $t('interface.dateNav.day') }}</button>
    </div>
  </div>
</template>

<script>
import { parseWeekMeta, collectYears, collectMonths, pickWeekInPeriod, ALL_TIME_RANGE } from '../utils/dateHierarchy';
import i18n from '../i18n'; // 直接引用 i18n 单例，用 tm() 取数组类消息（interface.months），避免依赖组件 $tm 全局（子组件被其它测试 mock $t 时不挂 $tm）

export default {
  props: {
    dateRanges: Array, // 接收从父组件传递来的日期范围
    defaultSelectedDateRange: String, // 接收父组件默认选中的日期范围
    showAllTimeTab: { type: Boolean, default: false }, // 是否在周 tab 最右渲染「全部时间」tab（仅搜索/标签页开启）
    rangeCounts: { type: Object, default: null }, // 各周 tab 下的命中条目数 { "YYYY-MM-DD - YYYY-MM-DD": 5 }，仅搜索/标签页传入
    allTimeCount: { type: Number, default: null }, // 全部时间 tab 下的命中条目数，仅搜索/标签页传入
    dayOptions: { type: Array, default: () => [] }, // 当前周内有 featured_at 的日（ymd 数组），由 ProductGrid 派生
    dayCounts: { type: Object, default: () => ({}) }, // 每个 ymd 对应的条目数量 { "2026-08-17": 3, ... }
    activeDay: { type: String, default: null }, // 受控：当前选中的日（ymd 或 null）
    dayPanelOpenable: { type: Boolean, default: true } // 当前周是否有 featured_at 数据（控制「日期」按钮是否显示）
  },
  data() {
    return {
      selectedWeek: this.defaultSelectedDateRange, // 初始化选中周
      isLeftOverflow: false, // 左侧溢出状态
      isRightOverflow: false, // 右侧溢出状态
      hasScrolledToRightInitially: false, // 标记是否已经初次滚动到最右
      hasUserScrolled: false, // 标记用户是否已主动交互（点箭头/切日期），交互后不再强行跳回最右
      jumpPanelOpen: false, // 年/月快速跳转面板是否展开（默认收起，仅占周行高度）
      datePanelOpen: false, // 日期筛选面板是否展开（与 jumpPanelOpen 互斥）
      panelYear: null // 面板当前展示的年份（点击年份切换，与 selectedWeek 解耦：年份只换月份、不跳转）
    };
  },
  computed: {
    // 将模块级哨兵常量暴露给模板（Options API 模板无法访问 script 作用域的 import 变量）
    allTimeRange() {
      return ALL_TIME_RANGE;
    },
    // 由 dateRanges 推导：存在的年份（升序）
    yearOptions() {
      return collectYears(this.dateRanges);
    },
    // 由 dateRanges + panelYear 推导：该年有数据的月份（本地化标签，复用 interface.months）
    monthOptions() {
      if (!this.panelYear) return [];
      const months = i18n.global.tm('interface.months');
      return collectMonths(this.dateRanges, this.panelYear).map((m) => ({
        ym: `${this.panelYear}-${String(m).padStart(2, '0')}`,
        label: months[m - 1]
      }));
    },
    // 当前选中周所属年（从 selectedWeek 起始日反推）
    activeYear() {
      if (!this.selectedWeek) return null;
      return parseWeekMeta(this.selectedWeek).year;
    },
    // 当前选中周所属年月
    activeMonth() {
      if (!this.selectedWeek) return null;
      return parseWeekMeta(this.selectedWeek).ym;
    }
  },
  mounted() {
    this.$nextTick(() => {
      // 页面首次加载时自动滚动到最右侧
      if (!this.hasScrolledToRightInitially) {
        this.scrollToRight();
      }
      this.checkOverflow(); // 检查溢出状态
    });
  },
  methods: {
    // 核心：设置选中周 + 抛出 tab-selected + 滚动到可视区（不触发 GA）
    applyWeek(week) {
      this.hasUserScrolled = true; // 主动交互后，不再由 fonts.ready 补滚跳回最右
      this.selectedWeek = week;
      this.$emit('tab-selected', week);
      this.scrollWeekIntoView(week);
    },
    selectWeek(week) {
      this.applyWeek(week);
      this.$emit('day-selected', null); // 切周清日筛选（双保险；ProductGrid watch 也会清）

      // 添加统计事件
      if (window.gtag) {
        window.gtag('event', 'date_nav', {
          'event_category': 'Navigation',
          'event_label': week,  // 点击的具体日期
          'value': 1
        });
      }
    },
    // 选中「全部时间」tab：复用 applyWeek 设置选中态 + 抛出 tab-selected(ALL_TIME) + 滚动到可视区
    selectAllTime() {
      this.applyWeek(ALL_TIME_RANGE);
      this.$emit('day-selected', null); // 切到全部时间清日筛选

      if (window.gtag) {
        window.gtag('event', 'date_nav', {
          'event_category': 'Navigation',
          'event_label': ALL_TIME_RANGE,
          'value': 1
        });
      }
    },
    selectYear(year) {
      // 仅把面板内的月份切换为该年有效月份：不跳转周、不收起面板
      this.panelYear = year;
      if (window.gtag) {
        window.gtag('event', 'date_nav_year', {
          'event_category': 'Navigation',
          'event_label': String(year),
          'value': 1
        });
      }
    },
    selectMonth(ym) {
      this.hasUserScrolled = true; // 主动交互后，不再由 fonts.ready 补滚跳回最右
      // 跳转到该年-月的第一周（latest:false），而非最新周
      const target = pickWeekInPeriod(this.dateRanges, { ym, latest: false });
      if (!target) return;
      this.applyWeek(target);
      this.jumpPanelOpen = false; // 跳转后自动收起
      if (window.gtag) {
        window.gtag('event', 'date_nav_month', {
          'event_category': 'Navigation',
          'event_label': ym,
          'value': 1
        });
      }
    },
    toggleJumpPanel() {
      this.jumpPanelOpen = !this.jumpPanelOpen;
      if (this.jumpPanelOpen) {
        // 年月面板展开时，关闭日期面板（互斥，避免同时占两行）
        this.datePanelOpen = false;
        // 展开时，面板年份对齐到当前选中周所属年
        this.panelYear = this.activeYear;
      }
      if (window.gtag) {
        window.gtag('event', 'date_nav_jump_open', {
          'event_category': 'Navigation',
          'event_label': this.jumpPanelOpen ? 'open' : 'close',
          'value': 1
        });
      }
    },
    // 切换日期筛选面板（与年月面板互斥）
    toggleDatePanel() {
      if (!this.dayPanelOpenable) return; // 当前周无 featured_at 时不响应
      this.datePanelOpen = !this.datePanelOpen;
      if (this.datePanelOpen) {
        // 日期面板展开时，关闭年月面板（互斥）
        this.jumpPanelOpen = false;
      }
      if (window.gtag) {
        window.gtag('event', 'date_nav_day_panel_open', {
          'event_category': 'Navigation',
          'event_label': this.datePanelOpen ? 'open' : 'close',
          'value': 1
        });
      }
    },
    // 选中某日：emit day-selected(ymd)，不收起面板（允许连续对比）；activeDay 由父级受控更新
    selectDay(ymd) {
      this.$emit('day-selected', ymd);
      if (window.gtag) {
        window.gtag('event', 'date_nav_day', {
          'event_category': 'Navigation',
          'event_label': ymd,
          'value': 1
        });
      }
    },
    // 清除日筛选：emit day-selected(null)，不收起面板
    clearDay() {
      this.$emit('day-selected', null);
      if (window.gtag) {
        window.gtag('event', 'date_nav_day_clear', {
          'event_category': 'Navigation',
          'event_label': 'clear',
          'value': 1
        });
      }
    },
    // 日 chip 文案：数字 + 语言相关「日 / 일」后缀（en 无后缀）
    dayLabel(ymd) {
      return this.$t('interface.dateNav.dayFormat', { day: Number(ymd.slice(8)) });
    },
    // 格式化日期范围的显示文本（本地化：月份名 + 日，分隔与后缀随语言）
    formatRange(range) {
      const [startDate, endDate] = range.split(' - ');
      const start = new Date(startDate);
      const end = new Date(endDate);
      const months = i18n.global.tm('interface.months');
      return this.$t('interface.dateNav.weekRange', {
        sm: months[start.getMonth()],
        sd: start.getDate(),
        em: months[end.getMonth()],
        ed: end.getDate()
      });
    },
    scrollLeft() {
      this.hasUserScrolled = true; // 主动交互后，不再由 fonts.ready 补滚跳回最右
      const container = this.$refs.dateNavContainer;
      container.scrollBy({ left: -200, behavior: 'smooth' }); // 左滚动
    },
    scrollRight() {
      this.hasUserScrolled = true; // 主动交互后，不再由 fonts.ready 补滚跳回最右
      const container = this.$refs.dateNavContainer;
      container.scrollBy({ left: 200, behavior: 'smooth' }); // 右滚动
    },
    scrollToRight() {
      const container = this.$refs.dateNavContainer;
      if (!container || !this.dateRanges?.length) return;
      const apply = () => {
        // 用 requestAnimationFrame 延迟到浏览器完成布局计算后再设置 scrollLeft，
        // 避免「设scroll→checkOverflow隐藏箭头→容器变宽→scroll位置失效」的竞态
        requestAnimationFrame(() => {
          container.scrollLeft = container.scrollWidth - container.clientWidth;
          // 再等一帧，确保 Vue 处理完 v-if 显隐后的布局稳定
          requestAnimationFrame(() => {
            this.checkOverflow();
          });
        });
      };
      apply(); // 先按当前布局滚到最右
      this.hasScrolledToRightInitially = true;
      // 自定义字体异步加载会撑大各 span 宽度 → scrollWidth 变大；
      // 初次滚动发生在字体就绪前会导致最右项被推出可视区而截断，故在 fonts.ready 后补滚一次。
      // 用户已主动交互（点箭头/切日期）后不再强行跳回最右，避免打断。
      if (document.fonts && document.fonts.ready && !this.hasUserScrolled) {
        document.fonts.ready.then(() => {
          if (!this.hasUserScrolled) apply();
        });
      }
    },
    // 将指定周滚动到可视区中央（修复原 selectWeek 点击后不居中的历史 bug）
    scrollWeekIntoView(week) {
      // 全部时间 tab 在周 tab 最右，直接滚到容器最右使其完整可见
      if (week === ALL_TIME_RANGE) {
        this.scrollToRight();
        return;
      }
      this.$nextTick(() => {
        const container = this.$refs.dateNavContainer;
        const el = this.$refs[week] && this.$refs[week][0];
        if (!container || !el) return;

        const scrollPosition = el.offsetLeft - container.clientWidth / 2 + el.clientWidth / 2;
        const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        container.scrollTo({ left: scrollPosition, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    },
    checkOverflow() {
      const container = this.$refs.dateNavContainer;
      if (!container) return;
      const threshold = 0; // 阈值改为 0：只要还有 1px 可滚就显示箭头，配合「箭头在布局流中、不再叠在内容上」让箭头总是能引导用户

      // 左侧溢出判定：scrollLeft 大于 0
      this.isLeftOverflow = container.scrollLeft > threshold;

      // 右侧溢出判定：scrollWidth - clientWidth - scrollLeft 大于 0
      this.isRightOverflow = container.scrollLeft < container.scrollWidth - container.clientWidth - threshold;
    }
  },
  watch: {
    selectedWeek() {
      // 选中周变化（含点击周、父组件传入）时，面板年份跟随联动；
      // 用户主动点击年份改 panelYear 不会触发此监听（不修改 selectedWeek），故不互相打架
      this.panelYear = this.activeYear;
    },
    defaultSelectedDateRange(newVal) {
      this.selectedWeek = newVal; // 父组件的默认选中日期更新时，更新此值
      this.checkOverflow(); // 选中日期变化时，检查溢出状态
      if (newVal) {
        // 初次加载（尚未滚到最右）让最新日期贴右完整显示；用户后续切换历史日期再居中
        if (!this.hasScrolledToRightInitially) {
          this.scrollToRight();
        } else {
          this.scrollWeekIntoView(newVal); // 同步滚动到选中周
        }
      }
    },
    dateRanges(newVal) {
      if (newVal && newVal.length) {
        this.$nextTick(() => {
          this.scrollToRight();
          this.checkOverflow();
        });
      }
    },
    // 「日」按钮由父组件异步出现/隐藏：出现时布局变窄，初始 scrollLeft 失效；
    // 隐藏时若此前日面板开着则兜底收起。按钮出现且用户尚未交互前补滚到最右。
    dayPanelOpenable(openable) {
      if (!openable) {
        this.datePanelOpen = false;
      } else if (!this.hasUserScrolled) {
        this.$nextTick(() => this.scrollToRight());
      }
    }
  }
};
</script>

<style scoped>
/* 通用未选中样式 */
.date-span {
  @apply cursor-pointer transition-all duration-300 ease-in-out inline-block;
  padding: 0.4rem 0.85rem;
  line-height: 1.2;
  white-space: nowrap; /* 防止文字换行 */
  color: var(--text-muted, #8a8175);
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: 0.25rem;
}

.date-span:hover {
  color: var(--ink, #1b1815);
  background-color: var(--brass-tint-hover, #f4f1ea);
}

/* 选中状态样式 */
.date-span-active {
  @apply font-medium inline-block transition-all duration-300 ease-in-out;
  color: var(--ink, #1b1815);
  background-color: var(--brass-tint, #f6efe1); /* 极淡暖金底 */
  border: 1px solid var(--brass-border, #d8c9a6); /* 暖金细边 */
  padding: 0.4rem 0.85rem;
  line-height: 1.2;
  border-radius: 0.25rem; /* 圆角 */
  white-space: nowrap; /* 防止文字换行 */
}

/* 关键：flex 子项解除 min-width:auto，让 overflow-x:auto 真正生效、不会撑开页面 */
.date-scroll-wrap {
  min-width: 0;
}
.date-panel {
  min-width: 0;
}

/* 年/月面板分隔符 */
.date-divider {
  color: var(--text-muted, #8a8175);
  padding: 0 0.15rem;
  user-select: none;
}

/* 年月 / 收起 切换按钮 */
.date-jump-btn {
  @apply cursor-pointer transition-all duration-300 ease-in-out;
  padding: 0.4rem 0.85rem;
  line-height: 1.2;
  white-space: nowrap;
  color: var(--text-muted, #8a8175);
  background-color: transparent;
  border: 1px solid var(--brass-border, #d8c9a6);
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.date-jump-btn:hover {
  color: var(--ink, #1b1815);
  background-color: var(--brass-tint-hover, #f4f1ea);
  border-color: var(--brass-hairline, #cdbfa6);
}

/* 左右滚动箭头：暖灰，hover 转暖金 */
.date-arrow-icon {
  color: var(--text-muted, #8a8175);
  transition: color 0.3s ease;
}
.date-arrow-btn { cursor: pointer; }
.date-arrow-btn:hover .date-arrow-icon {
  color: var(--brass, #b39a6b);
}

  /* 隐藏滚动条的样式 */
  .scrollbar-hide {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;  /* Chrome, Safari, and Opera */
  }

  /* 窄屏：年月/日期 两个跳转按钮并列时缩小，避免溢出 */
  @media (max-width: 640px) {
    .date-jump-btn {
      padding: 0.3rem 0.6rem;
      font-size: 0.8rem;
    }
  }
</style>
